// src/components/routing/TeamFormModal.js
// --- UPDATE (FlowSense v3 · AI Explainer)
//   • FlowSense look: gradient accent, icon header, lead preview card that works in dark mode.
//   • Team lead can be any supervisory or field role: admin, manager, supervisor, staff, tech,
//     inspector (was admin / staff / tech only); role spellings normalised.
//   • Bug fixes: the Save button stayed disabled after a field was touched (errors held empty
//     strings, and the check counted keys); numeric lead ids never matched the <select> value so
//     the lead preview never showed; missing dates showed "Invalid Date".
//   • "Draft with FlowSense AI": writes a short description from the team name and lead; falls
//     back to a plain template when the AI is offline or not available to this role.
// --- END UPDATE
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    Button, FormControl, FormLabel, Input, Textarea, Select, useToast, VStack, Spinner,
    Text, HStack, Avatar, Badge, Alert, AlertIcon, Divider, Box, InputGroup,
    InputLeftElement, InputRightElement, IconButton, Tooltip
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon, InfoIcon } from '@chakra-ui/icons';
import * as teamService from '../../services/teamService';
import * as userService from '../../services/userService';
// --- UPDATE
import { Icon, Flex, useColorModeValue } from '@chakra-ui/react';
import { MdGroups, MdAutoAwesome } from 'react-icons/md';
import { askFlowSenseAi, classifyAiError } from '../../services/flowsenseAiService';
const ROLE_ALIASES = { technician: 'tech', field_tech: 'tech', fco: 'tech', administrator: 'admin', field_inspector: 'inspector', field_supervisor: 'supervisor', foreman: 'supervisor' };
const normRole = (r) => { const k = String(r || '').trim().toLowerCase().replace(/[\s-]+/g, '_'); return ROLE_ALIASES[k] || k; };
const LEAD_ROLES = ['admin', 'manager', 'supervisor', 'staff', 'tech', 'inspector'];
const fmtDate = (d) => { const t = d ? new Date(d) : null; return t && !Number.isNaN(t.getTime()) ? t.toLocaleDateString() : '—'; };
// --- END UPDATE

const TeamFormModal = ({ isOpen, onClose, onSave, currentTeam }) => {
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [teamLeadId, setTeamLeadId] = useState('');
    const [allUsers, setAllUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isFetchingUsers, setIsFetchingUsers] = useState(false);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const [isDirty, setIsDirty] = useState(false);
    const toast = useToast();
    const isEditing = currentTeam && currentTeam.id;
    // --- UPDATE
    const [isDrafting, setIsDrafting] = useState(false);
    const accent = useColorModeValue('linear(to-r, blue.500, purple.500)', 'linear(to-r, blue.400, purple.400)');
    const previewBg = useColorModeValue('blue.50', 'whiteAlpha.100');
    const mutedColor = useColorModeValue('gray.600', 'gray.400');
    // --- END UPDATE

    useEffect(() => {
        const fetchUsers = async () => {
            setIsFetchingUsers(true);
            try {
                const response = await userService.fetchUsers({ limit: 100 });
                const users = Array.isArray(response.data) ? response.data : [];
                // --- UPDATE: supervisory + field roles, normalised; missing status counts as active
                const eligibleUsers = users.filter(u =>
                    LEAD_ROLES.includes(normRole(u.role)) && (!u.status || u.status === 'active')
                );
                setAllUsers(eligibleUsers);
            } catch (error) {
                const errorMsg = error?.response?.data?.message || error?.message || 'Failed to fetch users';
                toast({
                    title: 'Failed to fetch users',
                    status: 'error',
                    description: errorMsg,
                    duration: 4000,
                    isClosable: true
                });
                setAllUsers([]);
            } finally {
                setIsFetchingUsers(false);
            }
        };

        if (isOpen) {
            fetchUsers();
            if (isEditing) {
                setName(currentTeam.name || '');
                setDescription(currentTeam.description || '');
                setTeamLeadId(currentTeam.team_lead_id || '');
            } else {
                resetForm();
            }
            setErrors({});
            setTouched({});
            setIsDirty(false);
        }
    }, [isOpen, currentTeam, isEditing, toast]);

    const resetForm = () => {
        setName('');
        setDescription('');
        setTeamLeadId('');
    };

    const validateField = (fieldName, value) => {
        let error = '';

        switch (fieldName) {
            case 'name':
                if (!value || !value.trim()) {
                    error = 'Team name is required';
                } else if (value.length < 3) {
                    error = 'Team name must be at least 3 characters';
                } else if (value.length > 100) {
                    error = 'Team name must not exceed 100 characters';
                } else if (!/^[a-zA-Z0-9\s-_]+$/.test(value)) {
                    error = 'Team name can only contain letters, numbers, spaces, hyphens, and underscores';
                }
                break;
            case 'description':
                if (value && value.length > 500) {
                    error = 'Description must not exceed 500 characters';
                }
                break;
            default:
                break;
        }

        return error;
    };

    const handleBlur = (fieldName) => {
        setTouched(prev => ({ ...prev, [fieldName]: true }));
        const value = fieldName === 'name' ? name : description;
        const error = validateField(fieldName, value);
        setErrors(prev => ({ ...prev, [fieldName]: error }));
    };

    const handleNameChange = (e) => {
        const value = e.target.value;
        setName(value);
        setIsDirty(true);
        if (touched.name) {
            const error = validateField('name', value);
            setErrors(prev => ({ ...prev, name: error }));
        }
    };

    const handleDescriptionChange = (e) => {
        const value = e.target.value;
        setDescription(value);
        setIsDirty(true);
        if (touched.description) {
            const error = validateField('description', value);
            setErrors(prev => ({ ...prev, description: error }));
        }
    };

    const handleTeamLeadChange = (e) => {
        setTeamLeadId(e.target.value);
        setIsDirty(true);
    };

    const validateForm = () => {
        const newErrors = {};
        const nameError = validateField('name', name);
        const descError = validateField('description', description);

        if (nameError) newErrors.name = nameError;
        if (descError) newErrors.description = descError;

        setErrors(newErrors);
        setTouched({ name: true, description: true });

        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            toast({
                title: 'Validation Error',
                description: 'Please fix the errors in the form',
                status: 'error',
                duration: 3000,
                isClosable: true
            });
            return;
        }

        setIsLoading(true);
        const teamData = {
            name: name.trim(),
            description: description.trim(),
            team_lead_id: teamLeadId || null
        };

        try {
            if (isEditing) {
                await teamService.updateTeam(currentTeam.id, teamData);
                toast({
                    title: 'Team updated successfully',
                    description: `"${teamData.name}" has been updated`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });
            } else {
                await teamService.createTeam(teamData);
                toast({
                    title: 'Team created successfully',
                    description: `"${teamData.name}" has been created`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true
                });
            }
            onSave();
            onClose();
        } catch (error) {
            const errorMsg = error?.response?.data?.message || error?.message || 'Unknown error occurred';
            let errorDescription = errorMsg;

            if (errorMsg.includes('duplicate') || errorMsg.includes('already exists')) {
                errorDescription = 'A team with this name already exists';
                setErrors(prev => ({ ...prev, name: 'This team name is already taken' }));
            }

            toast({
                title: 'Error saving team',
                description: errorDescription,
                status: 'error',
                duration: 5000,
                isClosable: true
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleClose = () => {
        if (isDirty) {
            if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
                onClose();
            }
        } else {
            onClose();
        }
    };

    const selectedTeamLead = allUsers.find(u => String(u.id) === String(teamLeadId)); // --- UPDATE: string-safe
    const hasErrors = Object.values(errors).some(Boolean); // --- UPDATE: was Object.keys(errors).length

    // --- UPDATE: draft a description with FlowSense AI (template fallback)
    const handleDraftDescription = async () => {
        if (!name.trim()) { toast({ title: 'Enter a team name first', status: 'info', duration: 2500 }); return; }
        const lead = selectedTeamLead ? `${selectedTeamLead.username} (${normRole(selectedTeamLead.role)})` : null;
        const template = `${name.trim()} — FlowSense field crew${lead ? ` led by ${lead}` : ''}. Responsibilities and service area: [fill in].`;
        setIsDrafting(true);
        try {
            const data = await askFlowSenseAi({
                module: 'teams',
                question: 'Write a 1–2 sentence description for this team for the FlowSense team list. Plain text, no quotes, no invented locations; leave a [service area] placeholder if unknown.',
                context: { team_name: name.trim(), team_lead: lead, current_description: description || null },
            });
            const text = String(data.answer || '').replace(/^["“]|["”]$/g, '').trim().slice(0, 500);
            setDescription(text || template);
            setIsDirty(true);
        } catch (err) {
            const e = classifyAiError(err);
            setDescription((d) => d || template);
            setIsDirty(true);
            toast({ title: 'Used a template instead', description: e.message, status: 'info', duration: 4000, isClosable: true });
        } finally {
            setIsDrafting(false);
        }
    };
    // --- END UPDATE

    const getFieldStatus = (fieldName) => {
        if (!touched[fieldName]) return null;
        return errors[fieldName] ? 'error' : 'success';
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="xl" closeOnOverlayClick={!isDirty}>
            <ModalOverlay backdropFilter="blur(8px)" bg="blackAlpha.400" />{/* --- UPDATE: FlowSense look */}
            <ModalContent borderRadius="2xl" overflow="hidden">
                <Box position="absolute" top={0} left={0} right={0} h="3px" bgGradient={accent} />
                <ModalHeader pt={5}>
                    <HStack spacing={3}>
                        <Flex bgGradient={accent} p={2} borderRadius="xl"><Icon as={MdGroups} color="white" boxSize={5} /></Flex>
                        <Text fontWeight="800">{isEditing ? 'Edit Team' : 'Create New Team'}</Text>
                        {isDirty && <Badge colorScheme="orange" borderRadius="full">Unsaved</Badge>}
                    </HStack>
                </ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <VStack spacing={5} align="stretch">
                        <FormControl isRequired isInvalid={touched.name && !!errors.name}>
                            <FormLabel>
                                Team Name
                                <Tooltip label="A unique name to identify this team">
                                    <InfoIcon ml={2} w={3} h={3} color="gray.500" />
                                </Tooltip>
                            </FormLabel>
                            <InputGroup>
                                <Input
                                    value={name}
                                    onChange={handleNameChange}
                                    onBlur={() => handleBlur('name')}
                                    placeholder="e.g., North Sector Crew"
                                    maxLength={100}
                                />
                                {getFieldStatus('name') === 'success' && (
                                    <InputRightElement>
                                        <CheckIcon color="green.500" />
                                    </InputRightElement>
                                )}
                                {getFieldStatus('name') === 'error' && (
                                    <InputRightElement>
                                        <CloseIcon color="red.500" />
                                    </InputRightElement>
                                )}
                            </InputGroup>
                            {touched.name && errors.name && (
                                <Text color="red.500" fontSize="sm" mt={1}>
                                    {errors.name}
                                </Text>
                            )}
                            <Text color="gray.500" fontSize="xs" mt={1}>
                                {name.length}/100 characters
                            </Text>
                        </FormControl>

                        <FormControl isInvalid={touched.description && !!errors.description}>
                            <FormLabel>
                                Description
                                <Tooltip label="Describe the team's responsibilities and service area">
                                    <InfoIcon ml={2} w={3} h={3} color="gray.500" />
                                </Tooltip>
                            </FormLabel>
                            {/* --- UPDATE: AI draft */}
                            <HStack justify="flex-end" mb={1}>
                                <Button size="xs" variant="ghost" colorScheme="purple" leftIcon={<Icon as={MdAutoAwesome} />} onClick={handleDraftDescription} isLoading={isDrafting} loadingText="Drafting…">
                                    Draft with FlowSense AI
                                </Button>
                            </HStack>
                            <Textarea
                                value={description}
                                onChange={handleDescriptionChange}
                                onBlur={() => handleBlur('description')}
                                placeholder="Team responsibilities and service area..."
                                rows={4}
                                maxLength={500}
                            />
                            {touched.description && errors.description && (
                                <Text color="red.500" fontSize="sm" mt={1}>
                                    {errors.description}
                                </Text>
                            )}
                            <Text color="gray.500" fontSize="xs" mt={1}>
                                {description.length}/500 characters
                            </Text>
                        </FormControl>

                        <FormControl>
                            <FormLabel>
                                Team Lead
                                <Tooltip label="Select a team leader who will oversee this team">
                                    <InfoIcon ml={2} w={3} h={3} color="gray.500" />
                                </Tooltip>
                            </FormLabel>
                            {isFetchingUsers ? (
                                <HStack justify="center" py={3}>
                                    <Spinner size="sm" />
                                    <Text fontSize="sm" color="gray.600">Loading users...</Text>
                                </HStack>
                            ) : allUsers.length === 0 ? (
                                <Alert status="warning" fontSize="sm">
                                    <AlertIcon />
                                    No eligible users found. Active admin, manager, supervisor, staff, technician and inspector accounts can be team leads.
                                </Alert>
                            ) : (
                                <>
                                    <Select
                                        placeholder="Select a team lead (optional)"
                                        value={teamLeadId}
                                        onChange={handleTeamLeadChange}
                                    >
                                        {allUsers.map(user => (
                                            <option key={user.id} value={user.id}>
                                                {user.username} ({normRole(user.role)}) - {user.email}
                                            </option>
                                        ))}
                                    </Select>
                                    {selectedTeamLead && (
                                        <Box mt={3} p={3} bg={previewBg} borderRadius="lg">{/* --- UPDATE: dark-mode aware */}
                                            <HStack>
                                                <Avatar size="sm" name={selectedTeamLead.username} />
                                                <Box flex={1}>
                                                    <Text fontWeight="bold" fontSize="sm">
                                                        {selectedTeamLead.username}
                                                    </Text>
                                                    <Text fontSize="xs" color={mutedColor}>
                                                        {selectedTeamLead.email}
                                                    </Text>
                                                </Box>
                                                <Badge colorScheme="blue" borderRadius="full">{normRole(selectedTeamLead.role)}</Badge>
                                            </HStack>
                                        </Box>
                                    )}
                                </>
                            )}
                        </FormControl>

                        {isEditing && currentTeam && (
                            <>
                                <Divider />
                                <Box>
                                    <Text fontSize="sm" fontWeight="bold" mb={2}>Team Information</Text>
                                    <VStack align="stretch" spacing={2}>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color="gray.600">Created:</Text>
                                            <Text fontSize="sm">{fmtDate(currentTeam.created_at)}</Text>
                                        </HStack>
                                        <HStack justify="space-between">
                                            <Text fontSize="sm" color="gray.600">Last Updated:</Text>
                                            <Text fontSize="sm">{fmtDate(currentTeam.updated_at)}</Text>
                                        </HStack>
                                    </VStack>
                                </Box>
                            </>
                        )}
                    </VStack>
                </ModalBody>
                <ModalFooter>
                    <Button variant="ghost" mr={3} onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button
                        colorScheme="blue"
                        onClick={handleSubmit}
                        isLoading={isLoading}
                        isDisabled={hasErrors || !name.trim()} /* --- UPDATE */
                        borderRadius="lg"
                    >
                        {isEditing ? 'Update Team' : 'Create Team'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

TeamFormModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    currentTeam: PropTypes.object,
};

export default TeamFormModal;